import * as React from "react";
import {
    Field,
    Input,
    InputOnChangeData,
    InfoLabel,
    makeStyles,
    Link,
    Checkbox,
    Text,
    CheckboxOnChangeData,
    MessageBar,
    MessageBarBody,
    MessageBarTitle
} from "@fluentui/react-components";
import { FluentDialog } from "../../components/FluentDialog";
import { flexColumn } from "../../styles/GlobalStyles";
import { EnvironmentIdentifier, TrialRequest } from "../../dto/TrialRequest";
import { ensureSuccess } from "../../util/ErrorHandling";
import { ISimpleRecord } from "../../dto/ISimpleRecord";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTurnstile } from "../../util/TurnstileEnv";
import { EnvironmentEntry, MultiEnvironmentInput } from "../../components/MultiEnvironmentInput";

export interface TrialDialogProps {
    productId: string;
    isOpen: boolean;
    close: (id?: string) => void;
    prefill?: TrialFormPrefill;
}

export interface TrialFormPrefill {
    emailAddress?: string;
    environments?: EnvironmentEntry[];
}

const useStyles = makeStyles({
    flexColumn
});

const maxEntries = 10;

export const TrialRequestDialog: React.FC<TrialDialogProps> = ({ 
    productId, 
    isOpen, 
    close, 
    prefill 
}) => {
    const styles = useStyles();
    const { siteKey } = useTurnstile();
    
    const [token, setToken] = React.useState<string>();
    const [emailAddress, setEmailAddress] = React.useState("");
    const [tenantId, setTenantId] = React.useState("");
    const [organizations, setOrganizations] = React.useState<EnvironmentEntry[]>([]);
    const [policiesAccepted, setPoliciesAccepted] = React.useState(false);
    const [requestPending, setRequestPending] = React.useState(false);
    const [error, setError] = React.useState("");

    // Apply prefill data when dialog opens or prefill changes
    React.useEffect(() => {
        if (isOpen && prefill) {
            if (prefill.emailAddress) {
                setEmailAddress(prefill.emailAddress);
            }
            
            const entraEnvironments = prefill?.environments?.filter(e => e.type === "entra") ?? [];
            
            if (entraEnvironments.length > 0) {
                setTenantId(entraEnvironments[0].identifier);
            }

            const dataverseEnvironments = prefill?.environments?.filter(e => e.type === "dataverse") ?? [];
            
            if (dataverseEnvironments.length > 0) {
                setOrganizations(dataverseEnvironments);
            }
        }
    }, [isOpen, prefill]);

    // Reset form when dialog closes
    React.useEffect(() => {
        if (!isOpen) {
            setEmailAddress("");
            setTenantId("");
            setOrganizations([]);
            setPoliciesAccepted(false);
            setToken(undefined);
            setError("");
        }
    }, [isOpen]);

    const requestTrial = React.useCallback(() => {
        const submit = async () => {
            try
            {
                setError("");
                setRequestPending(true);

                const request: TrialRequest = {
                    emailAddress,
                    environments: [
                        {
                            type: "entra",
                            identifier: tenantId,
                            name: "Entra ID Tenant"
                        },
                        ...(organizations.map(id => ({ type: id.type, identifier: id.identifier, name: id.name}) as EnvironmentIdentifier))
                    ],
                    productId,
                    policiesAccepted
                };
                
                const response = await fetch("/api/public/trialRequests", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },        
                    body: JSON.stringify({...request, token})
                });
                
                await ensureSuccess(response);

                const simpleRecord = await response.json() as ISimpleRecord;
                close(simpleRecord?.guid);
            }
            catch (e)
            {
                setError((e as Error)?.message);
            }
            finally
            {
                setRequestPending(false);
            }
        };

        submit();
    }, [emailAddress, tenantId, organizations, productId, policiesAccepted, token, close]);

    const onEmailChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setEmailAddress(data.value);
    }, []);

    const onTenantIdChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setTenantId(data.value);
    }, []);

    const onPoliciesAcceptedChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) =>
    {
        setPoliciesAccepted(data.checked as boolean);
    }, []);

    const onCancel = React.useCallback(() =>
    {
        close();
    }, [close]);

    return (
        <FluentDialog
            open={isOpen}
            title='Request trial'
            submitLabel="Ok"
            submitDisabled={!emailAddress || !tenantId || !organizations.length || !policiesAccepted || !token || requestPending}
            onSubmit={requestTrial}
            cancelLabel={"Cancel"}
            onCancel={onCancel}
        >
            <div className={styles.flexColumn}>
                { error && (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>An error occured</MessageBarTitle>
                            <Text>{error}</Text>
                        </MessageBarBody>
                    </MessageBar> )
                }

                <Field
                    required
                    label="Email Address"
                    hint={`${(emailAddress ?? "").length} / 254`}
                >
                    <Input value={emailAddress} onChange={onEmailChange} maxLength={254} />
                </Field>

                <Field
                    required
                    label={
                        <InfoLabel
                            info={
                                <>
                                    The Entra ID's associated Tenant ID. For information on where to find this, go to make.powerapps.com, click on the "Settings" cog, go to "Session Details" and copy the value after "Tenant ID" from there. Format without brackets, e.g. "aab856fc-aa99-ba1b-ba77-6e4480c70071".
                                </>
                            }
                        >
                            Tenant ID
                        </InfoLabel>
                    }
                    hint={`${(tenantId ?? "").length} / 36`}
                >
                    <Input value={tenantId} onChange={onTenantIdChange} maxLength={36}/>
                </Field>

                <MultiEnvironmentInput
                    value={organizations}
                    onChange={setOrganizations}
                    maxEntries={maxEntries}
                    type="dataverse"
                />

                <Text>
                    You are only able to request a trial for this product once using the supplied Tenant ID and Organization IDs.
                    Therefore, if you intend to try out the product in multiple environments, make sure to include all of them in your request.
                </Text>

                <Text>
                    To request a trial license, please read our <Link href='/legal/termsandconditions' target="_blank">Terms and Conditions</Link>, <Link href='/legal/acceptableusepolicy' target="_blank">Acceptable Use Policy</Link>, <Link href='/legal/privacypolicy' target="_blank">Privacy Policy</Link>, <Link href='/legal/cookiepolicy' target="_blank">Cookie Policy</Link>, <Link href='/legal/returnpolicy' target="_blank">Return Policy</Link> and <Link href='/legal/disclaimer' target="_blank">Disclaimer</Link>.
                    Personally identifiable information that you submit will automatically be deleted when you confirm the verification email and a trial license is generated for you.
                    If you do not confirm the verification email within an hour, all your information will be automatically deleted and no trial license will be issued.
                    If you do not agree, we won't be able to create a trial license for you.
                </Text>
                    
                <Field required>
                    <Checkbox checked={policiesAccepted} onChange={onPoliciesAcceptedChange} label="I have read and agree to the Terms and Conditions, Acceptable Use Policy, Privacy Policy, Cookie Policy, Return Policy and the Disclaimer." />
                </Field>

                <Turnstile siteKey={siteKey} onSuccess={setToken} />
            </div>
        </FluentDialog>
    );
};